import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

import { SignalWithAuditDto } from "@/types";
import { RelatedObjectType } from "@/enums";
import { SignalFormData } from "@/components/forms/create-signal-form";
import {
  createSignal,
  fetchSignalsByAccountId,
  fetchSignalById,
  fetchSignalRelatedObjects,
  updateSignal,
  UpdateSignalRequest,
  fetchTopGrowthSignalsByAccountId,
  fetchTopRiskSignalsByAccountId,
  fetchAllSignals,
  deleteSignal,
} from "@/services/frontendServices/signalsServices";
import { accountKeys } from "./useAccounts";

// Keys for queries, to maintain consistency
export const signalKeys = {
  all: ["signals"] as const,
  lists: () => [...signalKeys.all, "list"] as const,
  list: (accountId: string) => [...signalKeys.lists(), accountId] as const,
  details: () => [...signalKeys.all, "detail"] as const,
  detail: (id: string) => [...signalKeys.details(), id] as const,
};

// Hook to create new signal
export const useCreateSignal = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SignalWithAuditDto,
    Error,
    SignalFormData & { accountId: string }
  >({
    mutationFn: ({ accountId, ...formData }) =>
      createSignal(formData, accountId),
    onSuccess: (newSignal, variables) => {
      queryClient.setQueryData<SignalWithAuditDto[]>(
        signalKeys.list(variables.accountId),
        (oldData = []) => {
          return [newSignal, ...oldData];
        }
      );

      // Invalidates the account's signals list query to force a refetch
      queryClient.invalidateQueries({
        queryKey: signalKeys.list(variables.accountId),
      });

      // Invalidate top signals queries to ensure they update when new signals are created
      queryClient.invalidateQueries({
        queryKey: [...signalKeys.list(variables.accountId), "topGrowth"],
      });
      queryClient.invalidateQueries({
        queryKey: [...signalKeys.list(variables.accountId), "topRisk"],
      });

      // Invalidate account cache to update last updated date
      queryClient.invalidateQueries({
        queryKey: accountKeys.detail(variables.accountId),
      });

      toast.success("Signal saved successfully!", {
        description: `Signal has been created.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      let errorMessage = "An unexpected error occurred";

      // Check if error.message is a plain string or JSON
      if (error.message) {
        try {
          // Try to parse as JSON
          const errorDetails = JSON.parse(error.message);
          if (errorDetails.error && errorDetails.details) {
            errorMessage = `${errorDetails.error}: ${errorDetails.details}`;
          } else {
            errorMessage = errorDetails.error || error.message;
          }
        } catch {
          // If parsing fails, use the message as-is
          errorMessage = error.message;
        }
      }

      toast.error("Error", {
        description: `Creating signal: ${errorMessage}`,
        duration: 5000,
      });
    },
  });
};

// Hook to get all signals for a specific account
export const useSignalsByAccount = (accountId: string) => {
  return useQuery({
    queryKey: signalKeys.list(accountId),
    queryFn: () => fetchSignalsByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to get top 3 most recently added growth signals for a specific account
export const useTopGrowthSignals = (accountId: string) => {
  return useQuery<SignalWithAuditDto[], Error>({
    queryKey: [...signalKeys.list(accountId), "topGrowth"],
    queryFn: () => fetchTopGrowthSignalsByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to get top 3 most recently added risk signals for a specific account
export const useTopRiskSignals = (accountId: string) => {
  return useQuery<SignalWithAuditDto[], Error>({
    queryKey: [...signalKeys.list(accountId), "topRisk"],
    queryFn: () => fetchTopRiskSignalsByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useSignal = (signalId: string) => {
  return useQuery({
    queryKey: signalKeys.detail(signalId),
    queryFn: () => fetchSignalById(signalId),
    enabled: !!signalId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

interface UseSignalRelatedObjectsParams {
  signalId: string;
  types: RelatedObjectType[];
}

export function useSignalRelatedObjects({
  signalId,
  types,
}: UseSignalRelatedObjectsParams) {
  return useQuery({
    queryKey: ["signalRelatedObjects", signalId, types],
    queryFn: async () => {
      return fetchSignalRelatedObjects(signalId, types);
    },
    enabled: !!signalId && !!types.length,
  });
}

// Hook to update existing signal
export const useUpdateSignal = ({
  showSuccessToast = true,
}: { showSuccessToast?: boolean } = {}) => {
  const queryClient = useQueryClient();

  return useMutation<
    SignalWithAuditDto,
    Error,
    { signalId: string; formData: UpdateSignalRequest }
  >({
    mutationFn: ({ signalId, formData }) => updateSignal(signalId, formData),
    onSuccess: (updatedSignal, variables) => {
      const { signalId, formData } = variables;

      // Update the individual signal detail
      queryClient.setQueryData<SignalWithAuditDto>(
        signalKeys.detail(signalId),
        updatedSignal
      );

      // Update the specific account's signal list
      const accountId = updatedSignal.account?.id;
      if (accountId) {
        queryClient.setQueryData<SignalWithAuditDto[]>(
          signalKeys.list(accountId),
          (oldData) => {
            if (!oldData) return oldData;
            return oldData.map((signal) =>
              signal.id === updatedSignal.id ? updatedSignal : signal
            );
          }
        );
      }
      queryClient.setQueryData<SignalWithAuditDto[]>(
        [...signalKeys.all, "global"],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((signal) =>
            signal.id === updatedSignal.id ? updatedSignal : signal
          );
        }
      );

      // Invalidate related queries to force a refetch
      queryClient.invalidateQueries({
        queryKey: signalKeys.detail(signalId),
      });

      // Invalidate top signals queries to ensure they update when signals are updated
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: [...signalKeys.list(accountId), "topGrowth"],
        });
        queryClient.invalidateQueries({
          queryKey: [...signalKeys.list(accountId), "topRisk"],
        });
      }

      // Invalidate related objects queries to force a refetch
      queryClient.invalidateQueries({
        queryKey: ["signalRelatedObjects", signalId],
      });

      // Invalidate account cache to update last updated date
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });
      }

      if (!showSuccessToast) return;

      // Show specific messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archived" : "unarchived";
        toast.success(`Signal ${action} successfully!`, {
          description: `Signal has been ${action}.`,
          duration: 3000,
        });
      } else {
        toast.success("Signal updated successfully!", {
          description: `Signal has been updated.`,
          duration: 3000,
        });
      }
    },
    onError: (error, variables) => {
      let errorMessage = "An unexpected error occurred";
      try {
        const errorDetails = JSON.parse(error.message);
        if (errorDetails.error && errorDetails.details) {
          errorMessage = `${errorDetails.error}: ${errorDetails.details}`;
        } else {
          errorMessage = errorDetails.error || "Failed to update signal";
        }
      } catch (parseError) {
        logger.error("Failed to parse backend errors", parseError, {
          context: { source: "service", module: "useSignals" },
        });
      }

      // Show specific error messages for archive/unarchive operations
      const { formData } = variables;
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archive" : "unarchive";
        toast.error(`Failed to ${action} signal`, {
          description: errorMessage,
          duration: 5000,
        });
      } else {
        toast.error("Error updating signal", {
          description: errorMessage,
          duration: 5000,
        });
      }
    },
  });
};

/**
 * Hook to delete an existing signal.
 * Invalidates all related caches including account data.
 */
export const useDeleteSignal = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { signalId: string }>({
    mutationFn: async ({ signalId }) => {
      await deleteSignal(signalId);
    },
    onSuccess: (_, { signalId }) => {
      // Remove the signal from the detail cache
      queryClient.removeQueries({
        queryKey: signalKeys.detail(signalId),
      });

      // Invalidate all signal lists (this covers all account lists)
      queryClient.invalidateQueries({
        queryKey: signalKeys.all,
      });

      // Invalidate related objects queries
      queryClient.invalidateQueries({
        queryKey: ["signalRelatedObjects", signalId],
      });

      // Invalidate all accounts to update last updated dates
      queryClient.invalidateQueries({
        queryKey: accountKeys.all,
      });

      toast.success("Signal deleted successfully!", {
        description: "The signal has been deleted.",
        duration: 3000,
      });
    },
    onError: (error) => {
      let errorMessage = "An unexpected error occurred";
      try {
        const errorDetails = JSON.parse(error.message);
        if (errorDetails.error && errorDetails.details) {
          errorMessage = `${errorDetails.error}: ${errorDetails.details}`;
        } else {
          errorMessage = errorDetails.error || "Failed to delete signal";
        }
      } catch (parseError) {
        logger.error("Failed to parse backend errors", parseError, {
          context: { source: "service", module: "useSignals" },
        });
        errorMessage = error.message || "Failed to delete signal";
      }

      toast.error("Error deleting signal", {
        description: errorMessage,
        duration: 5000,
      });
    },
  });
};

// Hook to get all signals across all accounts for global dashboard statistics
export const useAllSignals = () => {
  return useQuery<SignalWithAuditDto[], Error>({
    queryKey: [...signalKeys.all, "global"],
    queryFn: () => fetchAllSignals(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};
